import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import { getLeavesSummary, exportLeaveSummary } from "../../api/services/hroneApi";
import { PrimaryButton } from "../../StyledCss/styledbtn";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";
import LoadingView from "../LoadingView";

import "./index.css";
import FailureView from "../FailureView";
const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
}; 
const LeavesSummary = () => {
  // The "Summary" tab itself is gated on LEAVE_SUMMARY_VIEW by the parent.
  // The Export button is a separate action gated on LEAVE_ALL_HISTORY_EXPORT
  // ("Export" toggle of the Leave Management module) — the same code the
  // backend's /export_leaves_summary endpoint now enforces, and the same one
  // the All History tab's Export button uses.
  const { hasPermission } = usePermissions();
  const canExportSummary = hasPermission(FEATURE_CODES.LEAVE_ALL_HISTORY_EXPORT);
  const date = new Date();
  const [startMonth, setStartMonth] = useState(date);
  const [endMonth, setEndMonth] = useState(date);
  const [leavesSummaryData, setLeavesSummaryData] = useState([]);
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [loadingStates, setLoadingStates] = useState({
      exportCustom: false,
    }); 
    const setLoading = (operation, isLoading) => {
      setLoadingStates((prev) => ({ ...prev, [operation]: isLoading }));
    };

  const formattedStartMonth = format(startMonth, "yyyy-MM");
  const formattedEndMonth = format(endMonth, "yyyy-MM");

  const fetchLeavesSummaryData = useCallback(async () => {
    const defaultData = [
      {
        employeeId: "N/A",
        employeeName: "No Data Available",
        leavesCount: "0",
      },
    ];
    try {
      setApiStatus(apiStatusConstants.inProgress);
      const response = await getLeavesSummary(formattedStartMonth, formattedEndMonth);
      if (response.status === 200) {
        const leavesSummary=await response.json()
        const data = leavesSummary.data;
        const updatedSummaryData = data.map((eachItem) =>
          convertKeysToCamelCase(eachItem)
        );
        setLeavesSummaryData(updatedSummaryData.length > 0 ? updatedSummaryData : defaultData);
      } else {
        setLeavesSummaryData(defaultData);
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      setLeavesSummaryData(defaultData);
    } finally {
      setApiStatus(apiStatusConstants.success);}
  }, [formattedStartMonth, formattedEndMonth]); // Removed `defaultData` from the dependencies



  useEffect(() => {
    fetchLeavesSummaryData();
  }, [fetchLeavesSummaryData]);


  const handleExportData = async () => {
  
    try {
        setLoading("exportCustom", true);
      const queryArg = {
        start_month: formattedStartMonth,
        end_month: formattedEndMonth,
      };
    const response = await exportLeaveSummary(queryArg);

      if (response.status === 200) {

        const contentDisposition = response.headers["content-disposition"];
        const filename = contentDisposition
          ?.split("filename=")[1]
          ?.replace(/"/g, "")
          .trim();
        const blob = response.data;
        const downloadLink = document.createElement("a");
        const blobUrl = URL.createObjectURL(blob);
        downloadLink.href = blobUrl;
        downloadLink.download = filename || "download.xlsx";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(blobUrl);
      } else {
        console.error("Error exporting data: ", response.message);
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    } 
    finally {
      setLoading("exportCustom", false);
    }
  };
  



  const columnsLeaveSummary = [
    {
      headerName: "EMP ID",
      field: "employeeId",
      floatingFilter: true,
      filter: true,
      width: 100,
      flex: 1,
      suppressMenu: true,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "NAME",
      field: "employeeName",
      filter: true,
      sortable: true,
      floatingFilter: true,
      tooltipField: "employeeName",
      flex: 1,
      cellStyle: { textAlign: "center" },
      suppressMenu: true
    },
    {
      headerName: "LEAVES COUNT",
      field: "leavesCount",
      floatingFilter: true,
      filter: true,
      width: 120,
      flex: 1,
      cellStyle: { textAlign: "center" },
      suppressMenu: true
    },
    {
      headerName: "WFH COUNT",
      field: "wfhCount",
      floatingFilter: true,
      filter: true,
      width: 120,
      flex: 1,
      cellStyle: { textAlign: "center" },
      suppressMenu: true
    },
  ];


  const selectDates = () => (
    <div className="d-flex align-items-center justify-content-end me-3">
      <div className="d-flex flex-row flex-wrap justify-content-end gap-2">
        <div className="month-picker ">
          <label className="mb-1">Start Month</label>
          <DatePicker
            selected={startMonth}
            onChange={(date) => {
              if (date) {
                setStartMonth(date);
                if (date > endMonth) {
                            setEndMonth(date);
                          }
              } else {
                setStartMonth(new Date());
              }
            }}
            showMonthYearPicker
            dateFormat="MMMM yyyy"
            className="date-input-label"
          />
        </div>
        <div className="month-picker ">
          <label className="mb-1">End Month</label>
          <DatePicker
            selected={endMonth}
            onChange={(date) => {
              if (date) {
                setEndMonth(date);
              } else {
                setEndMonth(new Date());
              }
            }}
            showMonthYearPicker
            dateFormat="MMMM yyyy"
            className="date-input-label"
            minDate={startMonth}

          />
        </div>
         {canExportSummary && (
          <div className="d-flex flex-column align-self-end me-3 pl-3 pl-3">
            <PrimaryButton
              style={{
                textAlign: "center",
                width: "auto",
              }}
              type="button"
              onClick={handleExportData}
            >
              {loadingStates.exportCustom
                          ? "Exporting..."
                          : "Export"}
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );

  const renderLeavesSummary = () => (
    <div
      className="ag-theme-alpine"
      style={{
        fontSize: "14px",
        margin: "10px 15px",
        fontFamily: "Arial, Helvetica, sans-serif",
        textAlign: "center",
      }}
    >
      <AgGridReact
        rowData={leavesSummaryData}
        columnDefs={columnsLeaveSummary}
        pagination={true}
        paginationPageSize={20}
        domLayout="autoHeight"
        enableCellTextSelection={true}
      ></AgGridReact>
    </div>
  );
  const renderData = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.success:
        return renderLeavesSummary();
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={fetchLeavesSummaryData} />;
      default:
        return null;
    }
  };

  return (
    <>
      
      {selectDates()}
      <div className="table-container">{renderData()}</div>

  
    </>
  );
  
};

export default LeavesSummary;
